import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { NgxSpinnerService } from 'ngx-spinner';
import { catchError, finalize, map } from 'rxjs/operators';
import allAlertsSampleData from 'src/assets/dummy-data/alert-details-locaton.json';
import { Observable, throwError } from 'rxjs';

@Injectable()
export class ProductionPlanningService {
  private activeRequests = 0;

  constructor(private http: HttpClient, private spinner: NgxSpinnerService) { }

  private showSpinner() {
    if (this.activeRequests === 0) {
      this.spinner.show();
    }
    this.activeRequests++;
  }

  private hideSpinner() {
    this.activeRequests--;
    if (this.activeRequests <= 0) {
      this.activeRequests = 0;
      this.spinner.hide();
    }
  }

  fetchData<T>(url: string, data: any, method: 'GET' | 'POST' = 'POST'): Observable<T> {
    this.showSpinner();

    let request: Observable<any>;

    if (method === 'POST') {
      request = this.http.post(url, data, { responseType: 'text' });
    } else {
      request = this.http.get(url, { responseType: 'text' });
    }

    return request.pipe(
      map((response) => {
        const cleanedResponse = this.replaceInvalidValues(response);
        let parsedData: T;
        try {
          parsedData = JSON.parse(cleanedResponse);
        } catch (error) {
          console.error('Error parsing response:', cleanedResponse);
          throw new Error('Invalid JSON response');
        }
        return parsedData;
      }),
      catchError((error) => {
        console.error('API error:', error);
        return throwError(() => new Error('API error occurred'));
      }),
      finalize(() => {
        this.hideSpinner();
      })
    );
  }

  replaceInvalidValues(response: string): string {
    // Replace "Infinity", "-Infinity", and "NaN" with "0"
    return response
      .replace(/-Infinity/g, '0')
      .replace(/Infinity/g, '0')
      .replace(/NaN/g, '0');
  }

  public fetchFilterData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/get-filter-data`;
    this.showSpinner();
    return this.http.post(url, payload).pipe(finalize(() => this.hideSpinner()));
  }

  public getPlantNetRequirementData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/get-plant-net_requirement`;
    return this.fetchData<any>(url, payload, 'POST');
  }

  public saveProdIndentData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/update-net_requirement`;
    return this.fetchData<any>(url, payload, 'POST');
  }

  public getWarehouseNetRequirementData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/get-warehouse-net_requirement`;
    this.showSpinner();
    return this.http.post(url, payload).pipe(finalize(() => this.hideSpinner()));
  }

  public downloadReportData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/get-reports-data`;
    this.showSpinner();
    return this.http.post(url, payload, { responseType: 'blob' }).pipe(finalize(() => this.hideSpinner()));
  }

  public downloadPlantNetRequirementData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/get-plant-net_requirement`;
    this.showSpinner();
    return this.http.post(url, payload, { responseType: 'blob' }).pipe(finalize(() => this.hideSpinner()));
  }

  public getReportTableData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/get-reports-data`;
    this.showSpinner();
    return this.http.post(url, payload).pipe(finalize(() => this.hideSpinner()));
  }

  public getMapNodesData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/get-map-view`;
    this.showSpinner();
    return this.http.post(url, payload).pipe(finalize(() => this.hideSpinner()));
  }

  public getNodeConnectionData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/get-node-connections`;
    this.showSpinner();
    return this.http.post(url, payload).pipe(finalize(() => this.hideSpinner()));
  }

  public getDetailedAlerts() {
    const result: any[] = [];

    allAlertsSampleData.all_alerts.forEach((node) => {
      const { node_code, node_id, node_type, location, alerts } = node;

      alerts.forEach((alert) => {
        const { alert_id, category, sub_category, severity, rule_name, alert_description } = alert;

        result.push({
          node_code,
          node_id,
          node_type,
          location,
          alert_id,
          category,
          sub_category,
          severity,
          rule_name,
          alert_description,
          notified_users_list: [
            { userName: 'Kyran Benson', email: 'kyran.benson@email.com' },
            { userName: 'Mark Cross', email: 'mark.cross@email.com' },
          ],
        });
      });
    });

    return result;
  }

  public getAlertsDetail(alertId: number, nodeType: string) {
    let jsonData = allAlertsSampleData;
    for (const node of jsonData.all_alerts) {
      if (node.node_type === nodeType) {
        const foundAlert = (node.alerts as any[]).find((alert) => alert.alert_id === alertId);
        if (foundAlert) {
          return {
            node_code: node.node_code,
            node_id: node.node_id,
            node_type: node.node_type,
            location: node.location,
            data: foundAlert,
          };
        }
      }
    }
    return null;
  }

  public getDemandTableData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/get-demand-data`;
    return this.fetchData<any>(url, payload, 'POST');
  }

  public getDemandFilterData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/get-demand-filter`;
    return this.fetchData<any>(url, payload, 'POST');
  }

  public getFrozenPlanFilterData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/get-production_actual-filter`;
    return this.fetchData<any>(url, payload, 'POST');
  }

  public getFrozenPlanTableData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/get-production_actual-data`;
    return this.fetchData<any>(url, payload, 'POST');
  }

  public getRequirementDetailsData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/get-requirement-details`;
    return this.fetchData<any>(url, payload, 'POST');
  }

  public getPivotExplorerData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/pivot-explorer`;
    return this.fetchData<any>(url, payload, 'POST');
  }

  public downloadPivotExplorerData(payload: any) {
    const url = `${environment.serverUrl}/production_planning/pivot-explorer`;
    this.showSpinner();
    return this.http.post(url, payload, { responseType: 'blob' }).pipe(finalize(() => this.hideSpinner()));
  }

  public runCalculations(backOrder: boolean, backlog: boolean) {
    const url = `${environment.serverUrl}/production_planning/run-calculations?back_order=${backOrder}&backlog=${backlog}`;
    return this.fetchData<any>(url, null, 'GET');
  }

  public runValidations() {
    const url = `${environment.serverUrl}/production_planning/run-validations`;
    this.showSpinner();
    return this.http.get(url, { responseType: 'blob' }).pipe(finalize(() => this.hideSpinner()));
  }

  public downloadReportOnRunCalc() {
    const url = `${environment.serverUrl}/production_planning/download-output`;
    this.showSpinner();
    return this.http.get(url, { responseType: 'blob' }).pipe(finalize(() => this.hideSpinner()));
  }

  public triggerAutomationJob(data: any) {
    const url = `${environment.serverUrl}/production_planning/trigger-automation-job`;
    return this.fetchData<any>(url, data, 'POST');
  }

  public getJobStatus(data: any) {
    const url = `${environment.serverUrl}/production_planning/get-job-status`;
    return this.fetchData<any>(url, data, 'POST');
  }
}
